'use client';
import React, { useEffect, useState, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { generateInterpretation } from '@/lib/scoring';
import { Dumbbell, HeartPulse, User, Globe, Copy, CheckCircle2, Eye, Activity, Zap, Flame, ChevronRight, AlertCircle, Loader2, Target, ChevronDown, ChevronUp, Clock, Info, Tag, Calendar, RotateCcw } from 'lucide-react';
import Link from 'next/link';

interface AssignedProgram {
  id: string;
  programTitle: string;
  programFocus: string;
  status: string;
  startDate: string;
  programData: any;
}

interface AthleteProfileData {
  id: string;
  name: string;
  sport: string;
  position: string;
  gradYear: number | null;
  bio: string;
  publicProfileEnabled: boolean;
  profileImageUrl: string | null;
  recruitingInfo: string;
  email: string | null;
}

interface EpsData {
  eps: number;
  engineScore: number;
  ignitionScore: number;
  bounceScore: number;
}

export default function AthleteDashboardPage() {
  const { user } = useAuth();
  const [athleteProfile, setAthleteProfile] = useState<AthleteProfileData | null>(null);
  const [assignedPrograms, setAssignedPrograms] = useState<AssignedProgram[]>([]);
  const [epsData, setEpsData] = useState<EpsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'program' | 'wellness' | 'profile' | 'recruiting'>('program');
  const [publicLinkCopied, setPublicLinkCopied] = useState(false);
  const [togglingPublic, setTogglingPublic] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [profileForm, setProfileForm] = useState<Partial<AthleteProfileData>>({});
  const [savingProfile, setSavingProfile] = useState(false);

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

  const load = useCallback(async () => {
    if (!user) return;
    const supabase = createClient();
    setLoading(true);

    const { data: ap } = await supabase
      .from('athlete_profiles')
      .select('id, name, sport, position, grad_year, bio, public_profile_enabled, profile_image_url, recruiting_info, email')
      .eq('user_id', user.id)
      .single();

    if (ap) {
      const profile: AthleteProfileData = {
        id: ap.id,
        name: ap.name ?? '',
        sport: ap.sport ?? '',
        position: ap.position ?? '',
        gradYear: ap.grad_year ?? null,
        bio: ap.bio ?? '',
        publicProfileEnabled: ap.public_profile_enabled ?? false,
        profileImageUrl: ap.profile_image_url ?? null,
        recruitingInfo: ap.recruiting_info ?? '',
        email: ap.email ?? user.email ?? null,
      };
      setAthleteProfile(profile);
      setProfileForm(profile);

      // Load assigned programs
      const { data: assignments } = await supabase
        .from('athlete_program_assignments')
        .select('id, program_title, program_focus, status, start_date, program_data')
        .eq('athlete_id', ap.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      setAssignedPrograms((assignments ?? []).map((a: any) => ({
        id: a.id,
        programTitle: a.program_title ?? 'Training Program',
        programFocus: a.program_focus ?? 'balanced',
        status: a.status,
        startDate: a.start_date,
        programData: a.program_data,
      })));

      // Load latest EPS
      const { data: session } = await supabase
        .from('test_sessions')
        .select('eps, engine_score, ignition_score, bounce_score')
        .eq('athlete_id', ap.id)
        .order('session_date', { ascending: false })
        .limit(1)
        .single();

      if (session) {
        setEpsData({
          eps: Number(session.eps),
          engineScore: Number(session.engine_score),
          ignitionScore: Number(session.ignition_score),
          bounceScore: Number(session.bounce_score),
        });
      }
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const togglePublicProfile = async () => {
    if (!athleteProfile) return;
    setTogglingPublic(true);
    const supabase = createClient();
    const newVal = !athleteProfile.publicProfileEnabled;
    const { error } = await supabase
      .from('athlete_profiles')
      .update({ public_profile_enabled: newVal })
      .eq('id', athleteProfile.id);

    if (!error) {
      setAthleteProfile(prev => prev ? { ...prev, publicProfileEnabled: newVal } : prev);
      setProfileForm(prev => ({ ...prev, publicProfileEnabled: newVal }));
    }
    setTogglingPublic(false);
  };

  const copyPublicLink = async () => {
    if (!athleteProfile) return;
    const link = `${siteUrl}/athlete/profile/${athleteProfile.id}`;
    try {
      await navigator.clipboard.writeText(link);
      setPublicLinkCopied(true);
      setTimeout(() => setPublicLinkCopied(false), 2500);
    } catch {
      setSaveMsg('Failed to copy link.');
    }
  };

  const saveProfile = async () => {
    if (!athleteProfile) return;
    setSavingProfile(true);
    setSaveMsg(null);
    const supabase = createClient();
    const { error } = await supabase
      .from('athlete_profiles')
      .update({
        name: profileForm.name,
        sport: profileForm.sport,
        position: profileForm.position,
        grad_year: profileForm.gradYear,
        bio: profileForm.bio,
        recruiting_info: profileForm.recruitingInfo,
        updated_at: new Date().toISOString(),
      })
      .eq('id', athleteProfile.id);

    if (error) {
      setSaveMsg('Failed to save profile: ' + error.message);
    } else {
      setSaveMsg('Profile saved successfully!');
      setAthleteProfile(prev => prev ? { ...prev, ...profileForm } as AthleteProfileData : prev);
      setTimeout(() => setSaveMsg(null), 3000);
    }
    setSavingProfile(false);
  };

  const profile = epsData ? generateInterpretation(epsData.engineScore, epsData.ignitionScore, epsData.bounceScore) : null;

  if (loading) {
    return (
      <AppLayout activePath="/athlete-dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="text-center space-y-3">
            <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm text-zinc-500">Loading your dashboard...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!athleteProfile) {
    return (
      <AppLayout activePath="/athlete-dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="text-center space-y-4 max-w-sm mx-auto">
            <AlertCircle size={32} className="mx-auto text-zinc-500" />
            <h3 className="text-lg font-semibold text-zinc-200">No Athlete Profile Found</h3>
            <p className="text-sm text-zinc-500">
              Your account is not yet linked to an athlete profile. If your coach sent you an invite link, please use that link to connect your account.
            </p>
            <a href="/login" className="btn-primary inline-flex items-center gap-2 text-sm">
              Go to Login
            </a>
          </div>
        </div>
      </AppLayout>
    );
  }

  const TABS = [
    { id: 'program' as const, label: 'My Program', icon: Dumbbell },
    { id: 'wellness' as const, label: 'Wellness', icon: HeartPulse },
    { id: 'profile' as const, label: 'My Profile', icon: User },
    { id: 'recruiting' as const, label: 'Recruiting', icon: Globe },
  ];

  return (
    <AppLayout activePath="/athlete-dashboard">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="page-title">Athlete Dashboard</h1>
            <p className="text-sm text-zinc-500 mt-1">
              {athleteProfile.name}
              {athleteProfile.sport ? ` · ${athleteProfile.sport}` : ''}
              {athleteProfile.position ? ` · ${athleteProfile.position}` : ''}
              {athleteProfile.gradYear ? ` · Class of ${athleteProfile.gradYear}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link href="/daily-wellness" className="btn-secondary flex items-center gap-1.5 text-sm">
              <span className="w-2 h-2 rounded-full" style={{ background: '#E10600', boxShadow: '0 0 6px rgba(225,6,0,0.8)' }} />
              Daily Check-In
            </Link>
            <Link href="/assessment-entry" className="btn-primary text-sm">+ New Assessment</Link>
          </div>
        </div>

        {/* EPS Summary Row */}
        {epsData && profile && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'EPS Score', value: epsData.eps, color: '#E10600', icon: Target },
              { label: 'Engine', value: epsData.engineScore, color: '#8B0000', icon: Flame },
              { label: 'Ignition', value: epsData.ignitionScore, color: '#E10600', icon: Zap },
              { label: 'Bounce', value: epsData.bounceScore, color: '#B3B3B3', icon: Activity },
            ].map(item => (
              <div
                key={item.label}
                className="rounded-xl p-4 flex items-center gap-3"
                style={{ background: 'rgba(20,20,20,0.85)', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: `${item.color}18`, border: `1px solid ${item.color}25` }}
                >
                  <item.icon size={16} style={{ color: item.color }} />
                </div>
                <div>
                  <p className="text-xs text-zinc-500">{item.label}</p>
                  <p className="text-xl font-black" style={{ color: item.color }}>{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tab Navigation */}
        <div
          className="flex gap-1 p-1 rounded-xl"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
        >
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
              style={activeTab === tab.id ? {
                background: 'linear-gradient(135deg, #E10600, #8B0000)',
                color: '#fff',
              } : { color: '#71717a' }}
            >
              <tab.icon size={14} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'program' && (
          <ProgramTab programs={assignedPrograms} athleteProfile={athleteProfile} />
        )}
        {activeTab === 'wellness' && (
          <WellnessTab athleteId={athleteProfile.id} userId={user?.id ?? null} />
        )}
        {activeTab === 'profile' && (
          <ProfileTab
            profileForm={profileForm}
            setProfileForm={setProfileForm}
            saveProfile={saveProfile}
            savingProfile={savingProfile}
            saveMsg={saveMsg}
          />
        )}
        {activeTab === 'recruiting' && (
          <RecruitingTab
            athleteProfile={athleteProfile}
            epsData={epsData}
            profile={profile}
            siteUrl={siteUrl}
            togglePublicProfile={togglePublicProfile}
            togglingPublic={togglingPublic}
            copyPublicLink={copyPublicLink}
            publicLinkCopied={publicLinkCopied}
          />
        )}
      </div>
    </AppLayout>
  );
}

// ── Program Tab ────────────────────────────────────────────────────────────────
function ProgramTab({ programs, athleteProfile }: { programs: AssignedProgram[]; athleteProfile: AthleteProfileData }) {
  if (programs.length === 0) {
    return (
      <div
        className="rounded-2xl p-10 text-center"
        style={{ background: 'rgba(20,20,20,0.85)', border: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
          style={{ background: 'rgba(225,6,0,0.1)', border: '1px solid rgba(225,6,0,0.2)' }}
        >
          <Dumbbell size={28} style={{ color: '#E10600' }} />
        </div>
        <h3 className="text-lg font-semibold text-zinc-200 mb-2">No Program Assigned Yet</h3>
        <p className="text-sm text-zinc-500 max-w-sm mx-auto">
          Your coach will assign a training program after completing your assessment. Check back soon!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {programs.map(prog => (
        <FullProgramCard key={prog.id} program={prog} />
      ))}
    </div>
  );
}

// ── Biotensegrity / pillar constants ──────────────────────────────────────────
const PILLAR_META: Record<string, { color: string; label: string; bg: string; Icon: React.ElementType }> = {
  engine:     { color: '#8B0000', label: 'Engine',     bg: 'rgba(139,0,0,0.1)',    Icon: Flame },
  ignition:   { color: '#E10600', label: 'Ignition',   bg: 'rgba(225,6,0,0.08)',   Icon: Zap },
  bounce:     { color: '#B3B3B3', label: 'Bounce',     bg: 'rgba(179,179,179,0.08)', Icon: Activity },
  integrated: { color: '#FF3B30', label: 'Integrated', bg: 'rgba(255,59,48,0.08)', Icon: Dumbbell },
};

const INTENT_COLORS: Record<string, string> = {
  tension: '#8B0000', stiffness: '#B3B3B3', recoil: '#E10600',
  acceleration: '#FF6B35', transfer: '#A855F7',
};

const FOCUS_COLOR: Record<string, string> = {
  engine: '#8B0000', ignition: '#E10600', bounce: '#B3B3B3', balanced: '#FF3B30',
};

// ── Bio tag badge ─────────────────────────────────────────────────────────────
function BioTagBadge({ tag }: { tag: any }) {
  const pm = PILLAR_META[tag.pillar] ?? PILLAR_META.engine;
  const ic = INTENT_COLORS[tag.intent] ?? '#71717a';
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium" style={{ background: `${pm.color}18`, color: pm.color }}>
        <Tag size={9} />{tag.pillar}
      </span>
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs" style={{ background: 'rgba(255,255,255,0.05)', color: '#71717a' }}>
        {tag.fascialFocus}
      </span>
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium" style={{ background: `${ic}15`, color: ic }}>
        <Target size={9} />{tag.intent}
      </span>
    </div>
  );
}

// ── Exercise row ──────────────────────────────────────────────────────────────
function ExerciseRow({ ex, showBioTags }: { ex: any; showBioTags: boolean }) {
  return (
    <tr className="border-b border-zinc-800/40 hover:bg-zinc-800/30 transition-colors">
      <td className="py-2.5 px-2 min-w-[160px]">
        <p className="text-zinc-200 font-medium text-sm">{ex.name}</p>
        {showBioTags && ex.tag && <BioTagBadge tag={ex.tag} />}
        {ex.intentCue && (
          <p className="text-xs text-zinc-600 mt-0.5 italic leading-tight">{ex.intentCue}</p>
        )}
      </td>
      <td className="py-2.5 px-2 font-mono tabular-nums text-zinc-300 text-center text-sm">{ex.sets}</td>
      <td className="py-2.5 px-2 font-mono tabular-nums text-zinc-300 whitespace-nowrap text-sm">{ex.reps}</td>
      <td className="py-2.5 px-2 font-mono text-zinc-400 whitespace-nowrap text-sm">
        <span className="flex items-center gap-1">
          <Clock size={10} className="text-zinc-600" />{ex.rest}
        </span>
      </td>
      <td className="py-2.5 px-2 font-mono text-xs whitespace-nowrap" style={{ color: '#E10600' }}>{ex.intensity}</td>
      <td className="py-2.5 px-2 text-zinc-500 text-xs max-w-[180px]">{ex.notes}</td>
    </tr>
  );
}

// ── Exercise table ────────────────────────────────────────────────────────────
function ExerciseTable({ exercises, showBioTags }: { exercises: any[]; showBioTags: boolean }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[560px]">
        <thead>
          <tr className="border-b border-zinc-800">
            {['Exercise', 'Sets', 'Reps', 'Rest', 'Intensity', 'Notes'].map(h => (
              <th key={h} className="text-left py-2 px-2 text-xs font-medium uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {exercises.map((ex: any, i: number) => (
            <ExerciseRow key={ex.id ?? i} ex={ex} showBioTags={showBioTags} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Workout day card ──────────────────────────────────────────────────────────
function WorkoutDayCard({ day, showBioTags, defaultOpen = false }: { day: any; showBioTags: boolean; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const [showStructure, setShowStructure] = useState(false);
  const pm = PILLAR_META[day.pillar] ?? PILLAR_META.engine;
  const PillarIcon = pm.Icon;

  const exercises: any[] = day.exercises ?? [];
  const prepExercises = exercises.filter((ex: any) => ex.id?.startsWith('prep-'));
  const cooldownExercises = exercises.filter((ex: any) => ex.id?.startsWith('cool-'));
  const mainExercises = exercises.filter((ex: any) => !ex.id?.startsWith('prep-') && !ex.id?.startsWith('cool-'));

  return (
    <div className="border border-zinc-800 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3.5 bg-zinc-800/50 hover:bg-zinc-800 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: pm.bg, border: `1px solid ${pm.color}30` }}>
            <PillarIcon size={15} style={{ color: pm.color }} />
          </div>
          <div>
            <p className="text-sm font-semibold text-zinc-200">{day.day}</p>
            <p className="text-xs text-zinc-500">{day.label}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs px-2 py-0.5 rounded-full font-medium hidden sm:block" style={{ background: pm.bg, color: pm.color }}>
            {pm.label}
          </span>
          <span className="text-xs text-zinc-600 font-mono">{mainExercises.length} exercises</span>
          {open ? <ChevronUp size={14} className="text-zinc-500" /> : <ChevronDown size={14} className="text-zinc-500" />}
        </div>
      </button>

      {open && (
        <div className="p-4 space-y-4">
          {/* Focus */}
          {day.focus && (
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <Info size={11} style={{ color: pm.color }} />
              <span>{day.focus}</span>
            </div>
          )}

          {/* Session Structure Toggle */}
          <button
            onClick={() => setShowStructure(!showStructure)}
            className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            <ChevronRight size={11} className={`transition-transform ${showStructure ? 'rotate-90' : ''}`} />
            Session Structure
          </button>
          {showStructure && (
            <div className="pl-4 space-y-1">
              {[
                '1. Readiness check',
                '2. Biotensegrity movement prep',
                '3. Sprint or plyometric primer',
                '4. Main strength/power block',
                '5. Sprint/jump/med ball transfer block',
                '6. Accessory support',
                '7. Cooldown/reset',
              ].map((step, i) => (
                <p key={i} className="text-xs text-zinc-500">{step}</p>
              ))}
            </div>
          )}

          {/* Movement Prep */}
          {prepExercises.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Movement Prep</p>
              <ExerciseTable exercises={prepExercises} showBioTags={false} />
            </div>
          )}

          {/* Main Block */}
          {mainExercises.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Main Block</p>
              <ExerciseTable exercises={mainExercises} showBioTags={showBioTags} />
            </div>
          )}

          {/* Cooldown */}
          {cooldownExercises.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Cooldown / Reset</p>
              <ExerciseTable exercises={cooldownExercises} showBioTags={false} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Full program card ─────────────────────────────────────────────────────────
function FullProgramCard({ program }: { program: AssignedProgram }) {
  const [activeWeek, setActiveWeek] = useState(1);
  const [showBioTags, setShowBioTags] = useState(true);

  const color = FOCUS_COLOR[program.programFocus] ?? '#E10600';
  const programData = program.programData;

  // Support both array-of-weeks (new format) and legacy formats
  const weeks: any[] = Array.isArray(programData)
    ? programData
    : programData?.weeks ?? [];

  const weeklyLayout: string[] = programData?.weeklyLayout ?? [];
  const progressionModel: string[] = programData?.progressionModel ?? [];
  const retestRecommendation: string = programData?.retestRecommendation ?? '';
  const rationale: string = programData?.rationale ?? '';
  const daysPerWeek: number = programData?.daysPerWeek ?? 0;
  const duration: number = programData?.duration ?? weeks.length;

  const currentWeekData = weeks.find((w: any) => (w.weekNumber ?? w.week) === activeWeek) ?? weeks[0];

  return (
    <div
      className="rounded-2xl overflow-hidden space-y-4"
      style={{ background: 'rgba(20,20,20,0.85)', border: '1px solid rgba(255,255,255,0.06)' }}
    >
      {/* Program Header */}
      <div className="p-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: `${color}18`, border: `1px solid ${color}25` }}
            >
              <Dumbbell size={18} style={{ color }} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-zinc-200">{program.programTitle}</h3>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span
                  className="text-xs font-medium capitalize px-2 py-0.5 rounded-full"
                  style={{ background: `${color}18`, color }}
                >
                  {program.programFocus}
                </span>
                {daysPerWeek > 0 && (
                  <span className="text-xs text-zinc-500">{daysPerWeek} days/week</span>
                )}
                {duration > 0 && (
                  <span className="text-xs text-zinc-500">{duration} weeks</span>
                )}
                <span className="text-xs text-zinc-500">
                  Started {new Date(program.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
            </div>
          </div>
        </div>
        {rationale && (
          <p className="text-xs text-zinc-500 mt-3 leading-relaxed">{rationale}</p>
        )}
      </div>

      <div className="px-5 pb-5 space-y-5">
        {/* Weekly Layout */}
        {weeklyLayout.length > 0 && (
          <div
            className="rounded-xl p-4"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Calendar size={13} style={{ color: '#E10600' }} />
              <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Weekly Layout</h4>
            </div>
            <div className="space-y-1">
              {weeklyLayout.map((line: string, i: number) => (
                <p key={i} className={`text-xs ${line.startsWith('Notes:') ? 'text-zinc-600 italic' : 'text-zinc-400'}`}>{line}</p>
              ))}
            </div>
          </div>
        )}

        {/* Bio Tags Toggle + Week Selector */}
        {weeks.length > 0 && (
          <>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Weekly Schedule</h4>
              <button
                onClick={() => setShowBioTags(!showBioTags)}
                className="flex items-center gap-1.5 text-xs transition-colors"
                style={{ color: showBioTags ? '#E10600' : '#71717a' }}
              >
                <Tag size={11} />
                {showBioTags ? 'Hide Bio Tags' : 'Show Bio Tags'}
              </button>
            </div>

            {/* Week tabs */}
            <div className="flex flex-wrap gap-2">
              {weeks.map((w: any, i: number) => {
                const wNum = w.weekNumber ?? w.week ?? i + 1;
                return (
                  <button
                    key={`week-${wNum}`}
                    onClick={() => setActiveWeek(wNum)}
                    className="flex flex-col items-center px-3 py-2 rounded-lg text-xs font-medium transition-all duration-150"
                    style={activeWeek === wNum ? {
                      border: '1px solid rgba(225,6,0,0.35)',
                      background: 'rgba(225,6,0,0.1)',
                      color: '#E10600',
                    } : {
                      border: '1px solid rgba(255,255,255,0.06)',
                      background: 'rgba(255,255,255,0.03)',
                      color: '#71717a',
                    }}
                  >
                    <span className="font-mono font-bold">W{wNum}</span>
                    {w.theme && (
                      <span className="text-zinc-600 text-xs mt-0.5 hidden sm:block">{w.theme.split(' ')[0]}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Week theme banner */}
            {currentWeekData?.theme && (
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'rgba(225,6,0,0.06)', border: '1px solid rgba(225,6,0,0.15)' }}>
                <Info size={14} style={{ color: '#E10600' }} className="flex-shrink-0" />
                <p className="text-sm text-zinc-300">
                  <span className="font-semibold text-zinc-200">
                    Week {currentWeekData.weekNumber ?? currentWeekData.week} — {currentWeekData.theme}
                  </span>
                  {progressionModel.length > 0 && (
                    <>
                      {': '}
                      {progressionModel[Math.min((currentWeekData.weekNumber ?? currentWeekData.week ?? 1) - 1, progressionModel.length - 1)]}
                    </>
                  )}
                </p>
              </div>
            )}

            {/* Daily workout cards */}
            {currentWeekData && (
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">
                  Week {currentWeekData.weekNumber ?? currentWeekData.week} Workouts
                </h4>
                {(currentWeekData.days ?? []).map((day: any, idx: number) => (
                  <WorkoutDayCard
                    key={day.id ?? idx}
                    day={day}
                    showBioTags={showBioTags}
                    defaultOpen={idx === 0}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Progression Model */}
        {progressionModel.length > 0 && (
          <div
            className="rounded-xl p-4"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
          >
            <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <RotateCcw size={12} style={{ color: '#E10600' }} />
              Progression Model
            </h4>
            <div className="space-y-2">
              {progressionModel.map((step: string, i: number) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-500 text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-mono">
                    {i + 1}
                  </span>
                  <p className="text-xs text-zinc-400">{step}</p>
                </div>
              ))}
            </div>
            {retestRecommendation && (
              <div className="mt-3 pt-3 border-t border-zinc-800">
                <p className="text-xs text-zinc-500">
                  <span className="text-zinc-400 font-medium">Retest: </span>
                  {retestRecommendation}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Wellness Tab ───────────────────────────────────────────────────────────────
function WellnessTab({ athleteId, userId }: { athleteId: string; userId: string | null }) {
  return (
    <div className="space-y-4">
      <div
        className="rounded-2xl p-5"
        style={{ background: 'rgba(20,20,20,0.85)', border: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-zinc-200">Daily Wellness Check-In</h3>
          <span className="w-2 h-2 rounded-full" style={{ background: '#E10600', boxShadow: '0 0 6px rgba(225,6,0,0.8)' }} />
        </div>
        <p className="text-sm text-zinc-500 mb-4">
          Track your daily readiness, sleep, soreness, and mental state. Your coach can see your wellness trends.
        </p>
        <Link
          href="/daily-wellness"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200"
          style={{ background: 'linear-gradient(135deg, #E10600, #8B0000)', color: '#fff' }}
        >
          <HeartPulse size={15} />
          Submit Today's Check-In
        </Link>
      </div>
      <RecentWellnessHistory athleteId={athleteId} />
    </div>
  );
}

function RecentWellnessHistory({ athleteId }: { athleteId: string }) {
  const [checkins, setCheckins] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('daily_wellness_checkins')
      .select('checkin_date, readiness_score, readiness_status, energy_level, sleep_quality')
      .eq('athlete_id', athleteId)
      .order('checkin_date', { ascending: false })
      .limit(7)
      .then(({ data }) => {
        setCheckins(data ?? []);
        setLoading(false);
      });
  }, [athleteId]);

  if (loading) return null;
  if (checkins.length === 0) return null;

  const STATUS_COLOR: Record<string, string> = {
    green: '#10B981', yellow: '#F59E0B', orange: '#F97316', red: '#EF4444',
  };

  return (
    <div
      className="rounded-2xl p-5"
      style={{ background: 'rgba(20,20,20,0.85)', border: '1px solid rgba(255,255,255,0.06)' }}
    >
      <h3 className="text-sm font-semibold text-zinc-200 mb-4">Recent Check-Ins</h3>
      <div className="space-y-2">
        {checkins.map((c, i) => (
          <div
            key={i}
            className="flex items-center justify-between p-3 rounded-lg"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}
          >
            <div className="flex items-center gap-3">
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ background: STATUS_COLOR[c.readiness_status] ?? '#71717a' }}
              />
              <span className="text-xs text-zinc-400">
                {new Date(c.checkin_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs text-zinc-500">
              <span>Sleep: {c.sleep_quality}/5</span>
              <span>Energy: {c.energy_level}/5</span>
              <span className="font-bold" style={{ color: STATUS_COLOR[c.readiness_status] ?? '#71717a' }}>
                {c.readiness_score}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Profile Tab ────────────────────────────────────────────────────────────────
function ProfileTab({
  profileForm,
  setProfileForm,
  saveProfile,
  savingProfile,
  saveMsg,
}: {
  profileForm: Partial<AthleteProfileData>;
  setProfileForm: React.Dispatch<React.SetStateAction<Partial<AthleteProfileData>>>;
  saveProfile: () => void;
  savingProfile: boolean;
  saveMsg: string | null;
}) {
  const SPORTS = ['Baseball', 'Basketball', 'Football', 'Soccer', 'Softball', 'Track & Field', 'Volleyball', 'Wrestling', 'Lacrosse', 'Swimming', 'Tennis', 'Golf', 'Cross Country', 'Hockey', 'Other'];
  const CURRENT_YEAR = new Date().getFullYear();
  const GRAD_YEARS = Array.from({ length: 8 }, (_, i) => CURRENT_YEAR + i);

  return (
    <div
      className="rounded-2xl p-6 space-y-5"
      style={{ background: 'rgba(20,20,20,0.85)', border: '1px solid rgba(255,255,255,0.06)' }}
    >
      <h3 className="text-sm font-semibold text-zinc-200">Update Your Profile</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">Full Name</label>
          <input
            type="text"
            value={profileForm.name ?? ''}
            onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))}
            className="input-field w-full"
            placeholder="Your full name"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">Sport</label>
          <select
            value={profileForm.sport ?? ''}
            onChange={e => setProfileForm(p => ({ ...p, sport: e.target.value }))}
            className="input-field w-full"
          >
            <option value="">Select sport</option>
            {SPORTS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">Position</label>
          <input
            type="text"
            value={profileForm.position ?? ''}
            onChange={e => setProfileForm(p => ({ ...p, position: e.target.value }))}
            className="input-field w-full"
            placeholder="e.g. Shortstop, QB, Forward"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">Graduation Year</label>
          <select
            value={profileForm.gradYear ?? ''}
            onChange={e => setProfileForm(p => ({ ...p, gradYear: parseInt(e.target.value) || null }))}
            className="input-field w-full"
          >
            <option value="">Select year</option>
            {GRAD_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-zinc-400 mb-1.5">Bio</label>
        <textarea
          value={profileForm.bio ?? ''}
          onChange={e => setProfileForm(p => ({ ...p, bio: e.target.value }))}
          className="input-field w-full resize-none"
          rows={3}
          placeholder="Tell coaches about yourself..."
        />
      </div>

      {saveMsg && (
        <div
          className={`flex items-center gap-2 p-3 rounded-lg text-sm ${saveMsg.includes('Failed') ? 'bg-red-950/40 border border-red-800/50 text-red-300' : 'bg-emerald-950/40 border border-emerald-800/50 text-emerald-300'}`}
        >
          {saveMsg.includes('Failed') ? <AlertCircle size={14} /> : <CheckCircle2 size={14} />}
          {saveMsg}
        </div>
      )}

      <button
        onClick={saveProfile}
        disabled={savingProfile}
        className="px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center gap-2"
        style={{
          background: savingProfile ? 'rgba(225,6,0,0.3)' : 'linear-gradient(135deg, #E10600, #8B0000)',
          color: '#fff',
          cursor: savingProfile ? 'not-allowed' : 'pointer',
        }}
      >
        {savingProfile ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : 'Save Profile'}
      </button>

      <div
        className="rounded-xl p-4"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
      >
        <p className="text-xs text-zinc-500">
          <span className="text-zinc-400 font-medium">Note: </span>
          Coach-assigned fields (assigned coach, program creator, verified metrics) can only be updated by your coach.
        </p>
      </div>
    </div>
  );
}

// ── Recruiting Tab ─────────────────────────────────────────────────────────────
function RecruitingTab({
  athleteProfile,
  epsData,
  profile,
  siteUrl,
  togglePublicProfile,
  togglingPublic,
  copyPublicLink,
  publicLinkCopied,
}: {
  athleteProfile: AthleteProfileData;
  epsData: EpsData | null;
  profile: ReturnType<typeof generateInterpretation> | null;
  siteUrl: string;
  togglePublicProfile: () => void;
  togglingPublic: boolean;
  copyPublicLink: () => void;
  publicLinkCopied: boolean;
}) {
  const publicLink = `${siteUrl}/athlete/profile/${athleteProfile.id}`;

  return (
    <div className="space-y-4">
      {/* Public Profile Toggle */}
      <div
        className="rounded-2xl p-5"
        style={{ background: 'rgba(20,20,20,0.85)', border: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-zinc-200">Public Recruiting Profile</h3>
            <p className="text-xs text-zinc-500 mt-0.5">
              {athleteProfile.publicProfileEnabled
                ? 'Your profile is public and can be viewed by college coaches.' :'Your profile is private. Enable it to share with college coaches.'}
            </p>
          </div>
          <button
            onClick={togglePublicProfile}
            disabled={togglingPublic}
            className="relative w-12 h-6 rounded-full transition-colors duration-200 flex-shrink-0"
            style={{ background: athleteProfile.publicProfileEnabled ? '#E10600' : '#3f3f46' }}
          >
            <span
              className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200"
              style={{ transform: athleteProfile.publicProfileEnabled ? 'translateX(24px)' : 'translateX(0)' }}
            />
          </button>
        </div>

        {athleteProfile.publicProfileEnabled && (
          <div className="space-y-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <div
              className="flex items-center gap-2 p-3 rounded-xl"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <p className="flex-1 text-xs text-zinc-400 font-mono truncate">{publicLink}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={copyPublicLink}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200"
                style={publicLinkCopied ? {
                  background: 'rgba(16,185,129,0.1)',
                  border: '1px solid rgba(16,185,129,0.3)',
                  color: '#10B981',
                } : {
                  background: 'linear-gradient(135deg, #E10600, #8B0000)',
                  color: '#fff',
                }}
              >
                {publicLinkCopied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                {publicLinkCopied ? 'Copied!' : 'Copy Profile Link'}
              </button>
              <a
                href={publicLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: '#d4d4d8',
                }}
              >
                <Eye size={14} />
                Preview
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Profile Preview */}
      <div
        className="rounded-2xl p-5 space-y-4"
        style={{ background: 'rgba(20,20,20,0.85)', border: '1px solid rgba(255,255,255,0.06)' }}
      >
        <h3 className="text-sm font-semibold text-zinc-200">Profile Preview</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <div>
            <p className="text-xs text-zinc-500">Name</p>
            <p className="text-zinc-200 font-medium">{athleteProfile.name || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-zinc-500">Sport</p>
            <p className="text-zinc-200 font-medium">{athleteProfile.sport || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-zinc-500">Position</p>
            <p className="text-zinc-200 font-medium">{athleteProfile.position || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-zinc-500">Class of</p>
            <p className="text-zinc-200 font-medium">{athleteProfile.gradYear || '—'}</p>
          </div>
          {epsData && (
            <div>
              <p className="text-xs text-zinc-500">EPS Score</p>
              <p className="text-zinc-200 font-bold" style={{ color: '#E10600' }}>{epsData.eps}</p>
            </div>
          )}
          {profile && (
            <div>
              <p className="text-xs text-zinc-500">Profile Type</p>
              <p className="text-zinc-200 font-medium capitalize">{profile.programFocus}</p>
            </div>
          )}
        </div>
        {athleteProfile.bio && (
          <div>
            <p className="text-xs text-zinc-500 mb-1">Bio</p>
            <p className="text-sm text-zinc-400">{athleteProfile.bio}</p>
          </div>
        )}
      </div>
    </div>
  );
}